import { Constants, Flight, PurchaseData, Traveler } from './../shared';
import { WriteValueOverride } from './models';
import { NavigationStepComponent } from './navigation-step.component';
import { PurchaseNavigationService } from './purchase-navigation.service';
import { QuotingService } from './quoting.service';
import { Component, EventEmitter, OnDestroy, Output, ViewChild, OnInit } from '@angular/core';
import { FormGroup, FormArray, FormControl, AbstractControl } from '@angular/forms';
import { ApiQuoteResponse, IApiAcknowledgement, FlightRequirementTypes } from '@bhtp-client-api/base';
import {
    FlightBuilderComponent,
    FlightValidationStatus,
    FlightViewModel,
    PostponedFlightsConfig,
    postponedFlightsValidator,
    conditionalFlightsValidator,
    PostponedFlightsViewModel,
    PostponedFlightsWrapperConfig,
    PostponedFlightsWrapperControlNames
} from '@bhtp-client-web/component';
import { PackageDetails } from '@bhtp-client-api/purchase';
import * as moment from 'moment';
import { Observable, Subscription } from 'rxjs';

@Component({
    selector: 'flight-step',
    template: require('./flight-step.component.html')
})
export class FlightStepComponent extends NavigationStepComponent implements OnDestroy, OnInit {
    public form: FormGroup;
    public flightsForm: FormArray;
    public postponedFlightsConfig: PostponedFlightsWrapperConfig;
    public postponedFlightData: PostponedFlightsViewModel;
    public flightsNowToolTip: string;
    public showFlightBuilder: boolean;
    public messages: any;

    @Output() public productSwapRequested: EventEmitter<string> = new EventEmitter<string>();
    @Output() public quoteCompleted: EventEmitter<ApiQuoteResponse> = new EventEmitter<ApiQuoteResponse>();

    @ViewChild(FlightBuilderComponent) public flightBuilder: FlightBuilderComponent;

    private travelers: Array<Traveler>;
    private acknowledgements: Array<IApiAcknowledgement> = new Array<IApiAcknowledgement>();

    private minDate: string;
    private maxDate: string;

    private quotingSubscription: Subscription;

    // keep track of the number of flights to determine how many connections to quote
    private previousFlightCount: number;
    private initialFlightCount: number;

    public get postponedFormValue(): PostponedFlightsViewModel {
        let postponedForm: AbstractControl = this.form.get(PostponedFlightsWrapperControlNames.postponedFlights);

        if (postponedForm) {
            return postponedForm.value;
        }

        return undefined;
    }

    public get packageFlightsAreConditional(): boolean {
        return this.currentPackage.configuration.flightRequirementType === FlightRequirementTypes.conditional;
    }

    constructor(navigationService: PurchaseNavigationService, private quoteService: QuotingService) {
        super(navigationService);

        this.postponedFlightData = {
            noFlights: this.currentPurchaseData.overrideRequiresFlights,
            flightsLater: this.currentPurchaseData.futureFlightsExpected
        };

        this.pruneEmptyFlights();

        this.showFlightBuilder = !Boolean(this.packageFlightsAreConditional && this.currentPurchaseData.overrideRequiresFlights);
    }

    public ngOnInit(): void {
        this.populateMessaging();
    }

    public quoteBeforeContinue(): boolean {
        return true;
    }

    public populateMessaging(): void {
        this.messages = {
            'flightOrPostponeRequired': `Please add flights or check the box confirming that you don’t want to add flights.`
        };

        this.flightsNowToolTip = `If flight information is provided prior to the departure date, flights can be tracked and claims can be automatically created. Insuring more than 4 flights will incur additional premium charges.`;

        this.postponedFlightsConfig = {
            futureFlightsExpectedConfirmMessage: `Great! We'll be reminding you and/or your customer before their trip's departure to add their flights to their policy.`,
            noFutureFlightsExpectedConfirmMessage: `If their itinerary changes, you and/or your customer can always add flights to their policy before their departure date.`,
            flightsLaterRequired: 'Please confirm whether your customer will be flying. If they are flying, we will remind you and/or your customer to add flights to their policy. ',
            flightsNowPrompt: `I don't want to add my customer's flights right now.`,
            flightsLaterPrompt: `Will your customer be flying during their trip?`,
            flightsLaterPositive: `Yes`,
            flightsLaterNegative: `No`
        };
    }

    public ngOnDestroy(): void {
        if (this.quotingSubscription) {
            this.quotingSubscription.unsubscribe();
            this.quotingSubscription = null;
        }
    }

    public canContinue(): Observable<boolean> {
        if (this.showFlightBuilder) {
            let flightStatus: FlightValidationStatus = FlightViewModel.getFlightsValidationStatus(this.flightsForm, this.currentPackage.configuration.flightRequirementType);

            switch (flightStatus) {
                case FlightValidationStatus.Valid_SkipTripEligibility:
                    return Observable.of(true);
                case FlightValidationStatus.Valid_VerifyTripEligibility:
                    return this.flightBuilder.checkAllFlights();
                default:
                    return Observable.of(false);
            }
        } else {
            let postponedFormControl: AbstractControl = this.form.get(PostponedFlightsWrapperControlNames.postponedFlights);
            if (postponedFormControl) {
                return Observable.of(postponedFormControl.valid);
            } else {
                return Observable.of(false);
            }
        }
    }

    public writeValues(purchaseData: PurchaseData, override: WriteValueOverride = WriteValueOverride.Default): void {
        if (this.showFlightBuilder) {
            // copy over flight data
            let flightsStatus: FlightValidationStatus = FlightViewModel.getFlightsValidationStatus(this.flightsForm, this.currentPackage.configuration.flightRequirementType);

            if (flightsStatus === FlightValidationStatus.Valid_SkipTripEligibility ||
                flightsStatus === FlightValidationStatus.Valid_VerifyTripEligibility) {

                purchaseData.flights = new Array<Flight>();

                this.flightsForm.controls.forEach(fg => {
                    let flight: Flight = <Flight>fg.value;
                    if (flight) {
                        purchaseData.flights.push(flight);
                    }
                });

                // Find the first flight that has airline filled in
                let firstFlight: FlightViewModel = purchaseData.flights.find((flight: FlightViewModel) => { return Boolean(flight.airline); });
                if (firstFlight && !purchaseData.airline) {
                    purchaseData.airline = firstFlight.airline.name;
                    purchaseData.airlineNotProvided = false;
                }

                purchaseData.acknowledgements = this.acknowledgements;
            }

            // clear out postponed flight data
            purchaseData.overrideRequiresFlights = undefined;
            purchaseData.futureFlightsExpected = undefined;
        } else {
            // copy over postponed flight data
            let postponedFlightsData: PostponedFlightsViewModel = this.postponedFormValue;
            if (postponedFlightsData) {
                purchaseData.overrideRequiresFlights = postponedFlightsData.noFlights;
                purchaseData.futureFlightsExpected = postponedFlightsData.flightsLater;
            }

            if (!purchaseData.airline && (purchaseData.airlineNotProvided === null || purchaseData.airlineNotProvided === undefined)) {
                purchaseData.airlineNotProvided = true;
            }

            // clear out flights and acknowledgements
            purchaseData.flights = [];
            purchaseData.acknowledgements = undefined;
        }
    }

    public buildForm(): void {
        this.setupMinMaxDates();

        // travelers used to check if any are already insured
        // for a flight input here
        this.travelers = new Array<Traveler>();
        this.travelers.push(this.currentPurchaseData.primaryTraveler);
        this.currentPurchaseData.additionalTravelers.forEach(t => {
            this.travelers.push(t);
        });

        this.flightsForm = new FormArray([]);

        if (!this.currentPurchaseData.flights) {
            this.currentPurchaseData.flights = new Array<Flight>();
        }

        if (this.currentPurchaseData.flights.length === 0) {
            this.currentPurchaseData.flights.push(new Flight());
        }

        this.initialFlightCount = this.currentPurchaseData.flights.length;
        this.previousFlightCount = this.initialFlightCount;

        this.form = new FormGroup({
            flights: this.flightsForm
        });

        if (this.currentPackage.configuration.flightRequirementType === FlightRequirementTypes.conditional) {
            let previousNoFlightsValue: boolean = this.currentPurchaseData.overrideRequiresFlights;

            this.form.valueChanges.subscribe((value: any) => {
                if (value.postponedFlights) {
                    let postponed: PostponedFlightsViewModel = value.postponedFlights;
                    // Only perform these tasks if noFlights changed since the last event
                    if (postponed.noFlights !== previousNoFlightsValue) {
                        if (postponed.noFlights) {
                            this.toggleFlightsOff();
                        } else {
                            this.toggleFlightsOn();
                        }
                    }

                    previousNoFlightsValue = postponed.noFlights;
                }
            });

            this.form.setValidators([conditionalFlightsValidator()]);
        }
    }

    public onProductSwap(newRatingId: string): void {
        this.productSwapRequested.emit(newRatingId);
    }

    public onAcknowledgementsAccepted(acknowledgements: IApiAcknowledgement[]): void {
        this.acknowledgements = acknowledgements;
    }

    public onRequoteFlights(flightCount: number): void {
        /* If there is a quote, increase/decrease the connection count by the number of flights added/removed. If flights
            were removed below the initial number of flights, clear the connection count. Connection count is only set if
            there has been a quote
        */
        if (this.currentQuote && (this.currentQuote.flightConnectionCount || this.currentQuote.flightConnectionCount === 0)) {
            if (flightCount < this.initialFlightCount) {
                this.currentQuote.flightConnectionCount = null;
            } else if (flightCount < this.previousFlightCount) {
                const decreaseInFlights: number = this.previousFlightCount - flightCount;
                this.currentQuote.flightConnectionCount = this.currentQuote.flightConnectionCount - decreaseInFlights;
            } else {
                const increaseInFlights: number = flightCount - this.previousFlightCount;
                this.currentQuote.flightConnectionCount = this.currentQuote.flightConnectionCount + increaseInFlights;
            }

            this.quoteService.storeCurrentQuote(this.currentQuote);
        }

        this.previousFlightCount = flightCount;

        this.quotingStatusChanged.emit(true);

        // ignore any previous calls if another flight is added/removed
        if (this.quotingSubscription && !this.quotingSubscription.closed) {
            this.quotingSubscription.unsubscribe();
        }

        // calls quote service directly so flight count doesn't get saved
        this.quotingSubscription = this.quoteService.getQuote(this.currentPurchaseData, false, flightCount, false, true)
            .subscribe(result => {
                this.quoteCompleted.emit(result);
            },
            null,
            () => {
                this.quotingStatusChanged.emit(false);
            });
    }

    private toggleFlightsOn(): void {
        this.showFlightBuilder = true;
    }

    private toggleFlightsOff(): void {
        this.showFlightBuilder = false;
        this.pruneEmptyFlights();
    }

    private packageConfigValid(): boolean {
        return Boolean(this.currentPackage && this.currentPackage.configuration);
    }

    private checkBaseIncludedFlightsExists(): boolean {
        return this.packageConfigValid() && this.currentPackage.configuration.baseIncludedFlights >= 0;
    }

    private checkMaxFlightsExists(): boolean {
        return this.packageConfigValid() && this.currentPackage.configuration.maximumNumberFlightsTotal >= 0;
    }

    /**
     * removes empty flights until in compliance with the maxNumberFlightsTotal if possible.
     */
    private pruneEmptyFlights(): void {
        let flights: Flight[] = this.currentPurchaseData.flights;

        for (let i = flights.length - 1; i >= 0; i--) {
            let flight: Flight = flights[i];

            if (this.showFlightBuilder === false && this.checkBaseIncludedFlightsExists() &&
                flights.length > this.currentPackage.configuration.baseIncludedFlights) {
                if (Flight.isEmpty(flight)) {
                    this.flightBuilder.onRemoveFlight(i);
                }
            } else if (this.checkMaxFlightsExists() && flights.length > this.currentPackage.configuration.maximumNumberFlightsTotal) {
                if (Flight.isEmpty(flight)) {
                    this.flightBuilder.onRemoveFlight(i);
                }
            } else {
                return;
            }
        }

        this.currentPurchaseData.flights = flights;
    }

    private setupMinMaxDates(): void {
        // if departure and return dates are present, buffer the max and min dates
        // by a day to both sides
        if (this.currentPurchaseData) {
            if (this.currentPurchaseData.departureDate) {
                let departureDate = moment(this.currentPurchaseData.departureDate);
                if (departureDate.isValid()) {
                    departureDate = departureDate.startOf('day');

                    // if departure date is tomorrow, don't subtract a buffer day
                    if (departureDate.isSame(moment().add(1, 'day').startOf('day'))) {
                        this.minDate = departureDate.format(Constants.DATE_PARSE_FORMAT);
                    }
                    else {
                        this.minDate = departureDate.add(-1, 'day').startOf('day').format(Constants.DATE_PARSE_FORMAT);
                    }
                }
            }

            if (this.currentPurchaseData.returnDate) {
                let returnDate = moment(this.currentPurchaseData.returnDate);
                if (returnDate.isValid()) {
                    this.maxDate = returnDate.add(1, 'day').startOf('day').format(Constants.DATE_PARSE_FORMAT);
                }
            }
        }
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/purchase/flight-step.component.ts